/**
 * The authored SCALE — radius, spacing, type and touch, named by role.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 *
 * A raw scale already existed: seven spacing steps, nine radii, nine font
 * sizes. It did not help, because a raw scale answers "what sizes may I use"
 * and the question a call site actually asks is "how round is a card". With
 * nine legal answers and no guidance, every surface picked its own.
 *
 * Measured on the running product before this file existed:
 *
 *   /clients    9 corner radii, 26 padding values, 12 size+weight pairs
 *   /invoices   8 corner radii, 22 padding values, 12 size+weight pairs
 *
 * and of those size+weight pairs, 12px regular alone accounted for over two
 * thousand elements — so the page was over-specified and under-differentiated
 * at the same time. Three weights of one size were doing the hierarchy work
 * that size is for.
 *
 * This is the same move the colour layer made. `bgPrimary` stopped meaning
 * "white or gray-800" and started meaning "the surface role"; a radius stops
 * meaning "6px" and starts meaning "a control". The value can then change once,
 * centrally, instead of in 26 places.
 *
 * ---------------------------------------------------------------------------
 * MOBILE IS NOT A SEPARATE SCALE
 * ---------------------------------------------------------------------------
 *
 * The Capacitor app IS `apps/crm-web`, so every component here renders at both
 * 390px and 1600px. A second mobile scale would guarantee the two drift, so the
 * touch and type FLOORS live in this table and apply everywhere. A 44px target
 * is not a mobile concession — it is the minimum size of a thing a person aims
 * at, and a mouse user loses nothing by it.
 *
 * Floors were added after measuring the phone build: 80 of 113 tap targets on
 * /clients were under 44px, the smallest being the 18px client-name link that
 * is the primary action on every card, and /transactions rendered 70 elements
 * at 10–11px.
 */

/** Corner radius. Three roles, because there are three kinds of rounded thing. */
export const radiusScale = {
	/** Buttons, inputs, chips, badges, menu items — anything you act on. */
	'--radius-control': '0.375rem',
	/** Cards, panels, modals, trays — anything that CONTAINS. */
	'--radius-card': '0.5rem',
	/** Fully round: avatars, status pills, count bubbles. */
	'--radius-pill': '9999px',
	/** Deliberately square. Table cells, full-bleed sections, dividers. */
	'--radius-none': '0',
} as const;

/**
 * Spacing, named for the gap it fills rather than its size.
 *
 * Deliberately short. Every additional step is another decision a call site can
 * get wrong, and the 26 values measured above are what "use your judgement"
 * produces at scale.
 */
export const spacingScale = {
	/** Between a label and its value, an icon and its text. */
	'--space-hair': '0.25rem',
	/** Inside a control: button, input, chip. */
	'--space-control': '0.5rem',
	/** Table cell padding, and the gap between related controls. */
	'--space-cell': '0.75rem',
	/** Inside a card or panel. The most common value in the product. */
	'--space-card': '1rem',
	/** Between cards, and between a header and the content under it. */
	'--space-section': '1.5rem',
	/** Between major regions of a page. */
	'--space-page': '2rem',
} as const;

/**
 * Type, named by what the text IS.
 *
 * ---------------------------------------------------------------------------
 * SCANNING AND READING ARE DIFFERENT SIZES
 * ---------------------------------------------------------------------------
 *
 * The first cut of this table had one `body` size at 14px, and it was wrong in
 * a way worth recording, because the mistake is the ordinary one.
 *
 * An ERP grid is SCANNED. The user is comparing values down a column, not
 * reading sentences, and the density is the point — a row that is 15% taller is
 * 15% fewer rows before scrolling. 12px is right there, and the hierarchy
 * inside the data layer comes from colour and weight rather than size.
 *
 * A form value, a detail field, a description or a note is READ. Density buys
 * nothing, the text is often the only thing on the line, and 12px is mean —
 * particularly at 390px where there is no hover, no zoom and a longer viewing
 * distance.
 *
 * So there are two, and the name says which job it does. Collapsing them back
 * into one is how you get either an unreadable form or a wasteful grid.
 *
 * The floor is 0.75rem/12px and there is nothing below it. Anything smaller was
 * an inline arbitrary value (`text-[10px]`), which is how the hero label on the
 * mobile card ended up outside the scale entirely.
 */
export const typeScale = {
	/** Page title. One per screen. */
	'--text-title': '1.25rem',
	/** Section heading, card heading, modal title. */
	'--text-heading': '1rem',
	/** Text that is READ: form values, detail fields, descriptions, notes. */
	'--text-prose': '0.875rem',
	/** Text that is SCANNED: table cells, list rows, card values. The default. */
	'--text-body': '0.75rem',
	/** Field labels, column headers, button text. */
	'--text-label': '0.75rem',
	/**
	 * Metadata: timestamps, counts, helper text. Same size as body and label —
	 * inside the data layer the DIFFERENCE is colour and weight, not another
	 * size step. Adding one is what produced twelve size/weight pairs on a page.
	 */
	'--text-meta': '0.75rem',
} as const;

/**
 * Touch. A floor, not a suggestion.
 *
 * Apple's HIG says 44px, Material says 48px. We take 44 as the minimum a target
 * may ever be and 48 as what a primary action should be. Grow the HIT AREA with
 * padding rather than the visible control — a 16px icon centred in a 44px
 * button is correct; a 44px icon is not.
 */
export const touchScale = {
	'--tap-min': '2.75rem',
	'--tap-comfortable': '3rem',
} as const;

/**
 * The one height every default-size control obeys (owner decision
 * 2026-07-31): buttons, selects, inputs and typeaheads align to it instead
 * of each carrying its own idea of "medium". The root carries the DEFAULT
 * density tier's 36px; a `DensityScope` overrides it per tier (32/36/40).
 */
export const controlScale = {
	'--control-h': '2.25rem',
	/*
	 * The same idea one tier down, for the `xs` controls that make up a list's
	 * filter bar. Those sit deliberately below the header row above them, and
	 * each component used to carry its own idea of how tall that is — the bar
	 * rendered a 36px search box, 32px selects, a 34px single-select and a
	 * 40px multi-select on one row. Unlike `--control-h` this one does NOT
	 * move with density: the bar is already the compact tier.
	 */
	'--control-h-xs': '2rem',
} as const;

/**
 * MOTION. Three durations and two easings, because a transition answers two
 * questions and neither of them is "how many milliseconds".
 *
 * Before this, the only shared motion in the product was the `aura-*` keyframe
 * set built for the AI agent surfaces. Every other transition duration was
 * picked independently at its call site, which is how a dropdown and a slide-over
 * ended up moving at unrelated speeds.
 *
 * The durations are separated by feel rather than by arithmetic: 75ms reads as
 * instant feedback, 150ms reads as a transition, 250ms reads as something
 * travelling. Values between them are indistinguishable and only add choices.
 *
 * Easing carries direction. Something ENTERING decelerates into place
 * (ease-out); something LEAVING accelerates away (ease-in). Using one curve for
 * both makes arrivals feel sluggish and departures feel reluctant.
 */
export const motionScale = {
	/** Hover and active feedback on a control. */
	'--motion-instant': '75ms',
	/** Most transitions: dropdowns, tooltips, colour changes. */
	'--motion-quick': '150ms',
	/** Panels and sheets that move a meaningful distance. */
	'--motion-deliberate': '250ms',
	/** Entering: fast, then settling. */
	'--motion-enter': 'cubic-bezier(0, 0, 0.2, 1)',
	/** Leaving: gentle, then away. */
	'--motion-exit': 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

/** Everything this layer declares, ready to write to `:root`. */
export const scaleCssVars: Record<string, string> = {
	...radiusScale,
	...spacingScale,
	...typeScale,
	...touchScale,
	...controlScale,
	...motionScale,
};

/**
 * The raw steps these roles replace.
 *
 * Kept as a record rather than deleted, because the retirement rule (decision
 * D6) is migrate-then-remove: a name only disappears once nothing references
 * it. This is the list to work down, not a list to preserve.
 */
export const RETIRING_SCALE_NAMES: ReadonlyArray<{
	name: string;
	replacedBy: string;
}> = [
	{ name: '--radius-sm', replacedBy: '--radius-control' },
	{ name: '--radius-base', replacedBy: '--radius-control' },
	{ name: '--radius-md', replacedBy: '--radius-control' },
	{ name: '--radius-lg', replacedBy: '--radius-card' },
	{ name: '--radius-xl', replacedBy: '--radius-card' },
	{ name: '--radius-2xl', replacedBy: '--radius-card' },
	{ name: '--radius-3xl', replacedBy: '--radius-card' },
	{ name: '--radius-full', replacedBy: '--radius-pill' },
	{ name: '--spacing-xs', replacedBy: '--space-hair' },
	{ name: '--spacing-sm', replacedBy: '--space-control' },
	{ name: '--spacing-md', replacedBy: '--space-card' },
	{ name: '--spacing-lg', replacedBy: '--space-section' },
	{ name: '--spacing-xl', replacedBy: '--space-page' },
];
